import { useState } from "react";
import { Link } from "react-router-dom";
import { Shield, Check, ArrowRight, Heart, MessageSquare, Users, Briefcase, Home, Star, ShoppingBag, HandHeart, ChevronDown, Sparkles } from "lucide-react";

const STRIPE_LINK = "https://buy.stripe.com/aFacMY9M03JR4TUc10c7u0k";

const painPoints = [
  "You say yes when every cell in your body is screaming no.",
  "You apologize for having needs.",
  "You rehearse conversations in your head but never have them.",
  "You feel responsible for everyone else's feelings.",
  "You're exhausted from being \"the nice one.\"",
  "You know you need boundaries but don't have the WORDS.",
];

const scriptPreviews = [
  {
    category: "Work",
    icon: <Briefcase className="w-5 h-5" />,
    situation: "Your boss asks you to stay late — again",
    script: "\"I have a commitment this evening that I can't move. I can prioritize this first thing tomorrow morning — would that work?\"",
    color: "bg-blue-50 border-blue-200",
    iconBg: "bg-blue-100 text-blue-700",
  },
  {
    category: "Family",
    icon: <Home className="w-5 h-5" />,
    situation: "A parent guilt-trips you",
    script: "\"I love you and I love spending time with you. I'm doing my best to balance everything. Let's plan a time that works for both of us.\"",
    color: "bg-amber-50 border-amber-200",
    iconBg: "bg-amber-100 text-amber-700",
  },
  {
    category: "Friends",
    icon: <Users className="w-5 h-5" />,
    situation: "Pressured to go out when you need rest",
    script: "\"I'm staying in tonight — I need the rest. Have the best time! Send me pics.\"",
    color: "bg-green-50 border-green-200",
    iconBg: "bg-green-100 text-green-700",
  },
  {
    category: "Relationship",
    icon: <Heart className="w-5 h-5" />,
    situation: "Your partner dismisses your feelings",
    script: "\"When you say I'm overreacting, it makes me feel like my feelings don't matter to you. I need you to hear me, even if you don't fully understand.\"",
    color: "bg-rose-50 border-rose-200",
    iconBg: "bg-rose-100 text-rose-700",
  },
  {
    category: "Social",
    icon: <MessageSquare className="w-5 h-5" />,
    situation: "Someone comments on your body or food",
    script: "\"I'd prefer not to have comments about my body/food. Thanks for understanding.\"",
    color: "bg-purple-50 border-purple-200",
    iconBg: "bg-purple-100 text-purple-700",
  },
  {
    category: "Self",
    icon: <HandHeart className="w-5 h-5" />,
    situation: "You catch yourself over-apologizing",
    script: "Replace 'I'm sorry' with 'Thank you.' Instead of 'Sorry I'm late' — 'Thank you for waiting.'",
    color: "bg-teal-50 border-teal-200",
    iconBg: "bg-teal-100 text-teal-700",
  },
];

const faqs = [
  {
    q: "Is this a physical book or digital?",
    a: "It's a beautifully designed digital PDF workbook. You'll get instant access after purchase — download, print at home, and start using it today. No shipping, no waiting.",
  },
  {
    q: "What if I'm too scared to actually say these things?",
    a: "That's exactly why every script includes a 'Softer Stepping Stone' — a gentler version for when you're not ready for the full boundary yet. Plus, every script has a 'Make It Yours' section where you rewrite it in your own voice. Start soft. You'll get stronger.",
  },
  {
    q: "Will these scripts sound robotic or fake?",
    a: "These are starting points, not rigid rules. Each one is designed to sound natural and human. But the real magic is when you rewrite them in YOUR voice. The workbook gives you space for that on every single script.",
  },
  {
    q: "I'm an empath. Won't setting boundaries hurt the people I love?",
    a: "Here's the truth most empaths need to hear: boundaries don't push people away. Resentment does. The people who love you will adjust. The people who don't — well, that's information you need. This workbook helps you protect your relationships by protecting yourself first.",
  },
  {
    q: "How is this different from just Googling 'how to set boundaries'?",
    a: "Google gives you theory. This workbook gives you the exact WORDS. Not 'learn to say no' — but 'here's the sentence to say when your boss asks you to stay late.' Plus the self-assessment, affirmation practice, and family conversation guide make this a complete transformation system, not just a list of tips.",
  },
  {
    q: "What topics do the 20 scripts cover?",
    a: "Work (boss, coworkers, free labor), Family (guilt trips, criticism, intrusive questions, hosting, privacy), Friends (one-sided friendships, peer pressure, gossip, lending), Romantic Relationships (feelings dismissed, needing alone time), Social (interrupting, body comments, secrets, leaving events), and Self (over-apologizing). Essentially, every boundary conversation you've ever avoided.",
  },
  {
    q: "Can I share this with my therapist / counselor?",
    a: "Absolutely! Print a copy for your sessions. The 'Make It Yours' sections are perfect for working through with a professional.",
  },
  {
    q: "What's the 'I GET TO' philosophy?",
    a: "It's the foundation of all Mind Body Spirit HYGIENE™ Tools. Instead of 'I have to set boundaries' (which feels heavy), we reframe it: 'I GET TO set boundaries' — because having the self-awareness and courage to protect your peace is a privilege, not a punishment. The workbook weaves this mindset shift into every script.",
  },
];

const testimonialQuotes = [
  {
    quote: "I've been in therapy for years and STILL couldn't find the words to tell my mother to stop criticizing my parenting. Script #5 changed everything. I said it almost word-for-word.",
    name: "A recovering people pleaser",
  },
  {
    quote: "The 'Softer Stepping Stone' versions are genius. I wasn't ready for the full boundary with my boss, but the soft version? I used it Monday morning.",
    name: "An empath learning to protect her energy",
  },
  {
    quote: "Script #20 about over-apologizing hit me so hard I cried. Replacing 'I'm sorry' with 'Thank you' has been the simplest and most powerful change I've ever made.",
    name: "Someone who used to apologize for breathing",
  },
];

export default function BoundaryLanding() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="bg-[#efeae7] min-h-screen" data-testid="boundary-landing-page">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-[#CCFBF1]" data-testid="boundary-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/downloads/MBSH-logo-TM-e4476e.png"
              alt="Mind Body Spirit HYGIENE™ Tools"
              className="h-12 w-auto"
            />
          </Link>
          <nav className="flex items-center gap-4 md:gap-8">
            <Link to="/" className="font-sans text-[#068fa2] hover:text-[#25a1c2] text-sm transition-colors hidden md:block">
              All Products
            </Link>
            <Link to="/build-your-own" className="text-[#068fa2] hover:text-[#25a1c2] text-sm font-medium transition-colors" data-testid="boundary-nav-build-your-own">Build Your Own</Link>
            <a
              href={STRIPE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-5 py-2.5 font-sans font-medium text-sm transition-all hover:-translate-y-0.5"
              data-testid="header-buy-btn"
            >
              <ShoppingBag className="w-4 h-4" />
              Get the Workbook — $14
            </a>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 md:pt-36 md:pb-24 relative overflow-hidden" data-testid="boundary-hero">
        <div className="absolute inset-0 bg-gradient-to-br from-[#efeae7] via-white to-[#F0F5F4]" />
        <div className="max-w-5xl mx-auto px-6 md:px-12 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 mb-6">
              <span className="bg-[#25a1c2] text-white text-xs font-bold px-3 py-1.5 rounded-full tracking-wide uppercase">New Release</span>
              <span className="text-[#25a1c2] text-sm font-medium">Mind Body Spirit HYGIENE™ Tools</span>
            </div>

            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.08] text-[#042F2E] mb-6" data-testid="hero-title">
              You Know You Need Boundaries.<br />
              <span className="text-[#25a1c2]">You Just Don't Have the Words.</span>
            </h1>

            <p className="font-sans text-lg md:text-xl text-[#068fa2] leading-relaxed mb-8 max-w-2xl">
              <strong>The Boundary Builder</strong> gives you 20 real scripts — the exact sentences to say
              when your boss asks you to stay late, your mother guilt-trips you,
              your friend only calls when they need something, and 17 more situations you've been avoiding.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <a
                href={STRIPE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-sans font-medium text-lg transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
                data-testid="hero-buy-btn"
              >
                <ShoppingBag className="w-5 h-5" />
                Get the Workbook — $14
              </a>
              <Link
                to="/boundary-builder"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#068fa2] hover:bg-[#068fa2] hover:text-white text-[#068fa2] rounded-full px-8 py-4 font-sans font-medium transition-all"
                data-testid="hero-preview-btn"
              >
                Preview the Full Workbook
              </Link>
            </div>

            <div className="flex items-center gap-6 text-sm text-[#475569] flex-wrap">
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#25a1c2]" />
                <span>Instant PDF download</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#25a1c2]" />
                <span>20 real scripts</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#25a1c2]" />
                <span>Print at home</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points — "This is for you if..." */}
      <section className="py-16 md:py-24 bg-[#042F2E]" data-testid="pain-points-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">This Workbook Is For You If...</h2>
          <p className="text-[#5EEAD4] text-sm mb-10">If even ONE of these hits home, keep reading.</p>
          <div className="grid md:grid-cols-2 gap-4 text-left">
            {painPoints.map((point, i) => (
              <div key={i} className="flex items-start gap-3 bg-white/5 rounded-xl p-5 border border-white/10">
                <Check className="w-5 h-5 text-[#5EEAD4] mt-0.5 shrink-0" />
                <p className="text-white/90 font-sans text-sm leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
          <p className="text-[#5EEAD4] font-serif italic text-xl mt-10">
            "I GET TO say no. I GET TO protect my peace. I just need the words."
          </p>
        </div>
      </section>

      {/* What's Inside */}
      <section className="py-16 md:py-24" data-testid="whats-inside-section">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <div className="text-center mb-14">
            <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-4">What's Inside</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#042F2E] mb-4">
              Not Just Advice. Actual Words.
            </h2>
            <p className="font-sans text-lg text-[#068fa2] max-w-2xl mx-auto">
              Every other boundary resource tells you to "learn to say no." This one tells you exactly WHAT to say.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: "\"Where Are My Boundaries Weak?\" Assessment",
                description: "16-question self-assessment across Work, Family, Friends, and Self. Know exactly where to start before you read a single script.",
                icon: <Sparkles className="w-6 h-6" />,
              },
              {
                title: "20 Boundary Scripts with Exact Words",
                description: "Real situations. Real language. Each includes the people-pleasing response you're used to, the boundary language you GET TO use, a softer stepping stone, and an affirmation.",
                icon: <MessageSquare className="w-6 h-6" />,
              },
              {
                title: "\"I GET TO Say No\" Affirmation Practice",
                description: "12 affirmation cards specifically for boundary-setting. Read one each morning. Say it out loud. Let it rewire the guilt.",
                icon: <Heart className="w-6 h-6" />,
              },
              {
                title: "Family Boundary Conversation Guide",
                description: "6 scripts for the hardest boundaries — opening the conversation, naming the pattern, stating your need, handling pushback, and holding the line.",
                icon: <Home className="w-6 h-6" />,
              },
            ].map((item, i) => (
              <div key={i} className="bg-white p-8 rounded-2xl border border-[#CCFBF1] shadow-sm hover:shadow-md transition-all hover:-translate-y-1">
                <div className="w-12 h-12 bg-[#F0F5F4] rounded-xl flex items-center justify-center text-[#25a1c2] mb-4">
                  {item.icon}
                </div>
                <h3 className="font-serif text-xl text-[#042F2E] mb-2">{item.title}</h3>
                <p className="text-[#475569] text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Script Previews — show 6 samples */}
      <section className="py-16 md:py-24 bg-[#F0F5F4]" data-testid="script-previews-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-4">Script Previews</p>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#042F2E] mb-4">
              6 of 20 Scripts — Free Preview
            </h2>
            <p className="text-[#068fa2] text-sm">Here's a taste. The full workbook has 20 complete scripts with softer alternatives, affirmations, and rewrite space.</p>
          </div>

          <div className="space-y-4">
            {scriptPreviews.map((item, i) => (
              <div key={i} className={`${item.color} border rounded-2xl p-6 md:p-8`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${item.iconBg}`}>
                    {item.icon}
                  </div>
                  <span className="font-sans text-xs font-bold uppercase tracking-widest text-gray-500">{item.category}</span>
                </div>
                <h3 className="font-bold text-[#042F2E] mb-3">"{item.situation}"</h3>
                <div className="bg-white/70 rounded-xl p-4 border border-[#25a1c2]/10">
                  <p className="text-[#25a1c2] text-xs font-bold uppercase tracking-widest mb-1">What you GET TO say:</p>
                  <p className="text-[#068fa2] font-medium text-sm leading-relaxed">{item.script}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <p className="text-[#068fa2] mb-4 font-medium">+ 14 more scripts covering gossip, money, body comments, over-apologizing, and more...</p>
            <a
              href={STRIPE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-sans font-medium transition-all hover:-translate-y-1 shadow-lg hover:shadow-xl"
              data-testid="scripts-buy-btn"
            >
              Get All 20 Scripts — $14
              <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-16 md:py-24 bg-[#068fa2]" data-testid="social-proof-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-4">What Boundary Builders Are Saying</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonialQuotes.map((t, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6">
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-[#5EEAD4] fill-[#5EEAD4]" />
                  ))}
                </div>
                <p className="font-sans text-white/90 text-sm leading-relaxed mb-4 italic">"{t.quote}"</p>
                <p className="font-sans text-[#5EEAD4] text-xs font-medium">— {t.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Email Connection — Weeks 27-39 */}
      <section className="py-16 md:py-24 bg-white" data-testid="email-tie-in">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <p className="font-sans text-sm tracking-[0.2em] uppercase text-[#25a1c2] font-medium mb-4">Goes Deeper With</p>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#042F2E] mb-5">
            Part of the "I GET TO" Journey
          </h2>
          <p className="font-sans text-lg text-[#068fa2] mb-8 max-w-2xl mx-auto">
            The Boundary Builder is the perfect companion to <strong>Phase 3: Relationships</strong> (Weeks 27-39)
            of the free 52-week "I GET TO" email journey. Get the weekly emails free, use this workbook to go deeper.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={STRIPE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-[#25a1c2] hover:bg-[#068fa2] text-white rounded-full px-8 py-4 font-sans font-medium transition-all hover:-translate-y-1 shadow-lg"
              data-testid="email-section-buy-btn"
            >
              <ShoppingBag className="w-5 h-5" />
              Get the Workbook — $14
            </a>
            <Link
              to="/workbook?from=boundary-landing"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#068fa2] hover:bg-[#068fa2] hover:text-white text-[#068fa2] rounded-full px-8 py-4 font-sans font-medium transition-all"
            >
              Join the Free Email Journey
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-[#F0F5F4]" data-testid="boundary-faq-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-[#042F2E] mb-4">
              Questions You Might Have
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl border border-[#CCFBF1] overflow-hidden shadow-sm"
                data-testid={`faq-item-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-5 text-left"
                >
                  <h3 className="font-bold text-[#042F2E] pr-4 text-sm md:text-base">{faq.q}</h3>
                  <ChevronDown className={`w-5 h-5 text-[#25a1c2] shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5">
                    <p className="text-[#475569] text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#042F2E]" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <Shield className="w-14 h-14 text-[#5EEAD4] mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-6 leading-tight">
            You're Not Mean.<br />You're Growing.
          </h2>
          <p className="text-white/70 text-lg mb-4 max-w-xl mx-auto">
            For $14, you get the exact words for 20 conversations you've been avoiding.
            The assessment, the scripts, the affirmations, the family guide — everything you need to start protecting your peace <strong className="text-white">today</strong>.
          </p>
          <p className="text-[#5EEAD4] font-serif italic text-xl mb-10">
            "I GET TO say no. I GET TO choose me. I GET TO protect my peace."
          </p>
          <a
            href={STRIPE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-white hover:bg-[#F0FDF4] text-[#25a1c2] rounded-full px-10 py-5 font-sans font-bold text-lg transition-all hover:-translate-y-1 shadow-xl hover:shadow-2xl"
            data-testid="final-buy-btn"
          >
            <ShoppingBag className="w-5 h-5" />
            Get The Boundary Builder — $14
          </a>
          <p className="text-white/40 text-xs mt-6">Instant PDF download. Print at home. Start today.</p>
        </div>
      </section>

      {/* Footer */}
    </div>
  );
}
